/**
 * @fileoverview Pure UGC cost math for the UGC Cost Calculator.
 * @description Rate-card bands, rights/whitelist uplifts, DIY vs agency vs
 * hybrid AI+creator paths, and cost-per-winner framing — extracted so unit
 * tests cover the arithmetic without pulling React into the Jest runtime.
 * @module components/resources/calculators/UgcCostCalculator.logic
 * @exports CREATOR_TIERS
 * @exports RIGHTS_TERMS
 * @exports NICHES
 * @exports PRODUCTION_MODES
 * @exports RATE_SOURCES
 * @exports HEALTHY_MONTHLY_CONCEPTS_MIN
 * @exports HEALTHY_MONTHLY_CONCEPTS_MAX
 * @exports calculateUgcCost
 * @exports formatUsdRange
 * @exports clampCreatives
 * @exports clampWinRate
 * @exports bundleDiscountFraction
 * @exports AGENCY_TYPICAL_RETAINER_HIGH
 * @exports AI_DISCOVERY_VARIANTS_PER_UNIT
 */

/**
 * Creator experience tier used for the organic-only base rate band.
 *
 * @typedef {'entry' | 'mid' | 'specialist'} CreatorTier
 */
export type CreatorTier = 'entry' | 'mid' | 'specialist'

/**
 * Paid-usage rights duration. Organic-only is included in the base rate.
 *
 * @typedef {'organic' | 'paid_6mo' | 'paid_12mo' | 'perpetual'} RightsTerm
 */
export type RightsTerm = 'organic' | 'paid_6mo' | 'paid_12mo' | 'perpetual'

/**
 * Vertical niche that nudges the base band (beauty/tech pay a premium).
 *
 * @typedef {'general' | 'beauty' | 'tech_saas' | 'finance' | 'food'} Niche
 */
export type Niche = 'general' | 'beauty' | 'tech_saas' | 'finance' | 'food'

/**
 * How the brand produces the monthly slate.
 *
 * @typedef {'diy_creators' | 'agency' | 'hybrid_ai'} ProductionMode
 */
export type ProductionMode = 'diy_creators' | 'agency' | 'hybrid_ai'

/**
 * Inclusive USD band (low / high) used throughout the rate card.
 *
 * @interface UsdBand
 * @property {number} low - Inclusive lower bound in USD.
 * @property {number} high - Inclusive upper bound in USD.
 */
export interface UsdBand {
  low: number
  high: number
}

/**
 * Published source citation backing a rate-card figure.
 *
 * @interface RateSource
 * @property {string} id - Short stable id used in UI footnotes.
 * @property {string} label - Human-readable publisher + year.
 * @property {string} url - Canonical URL for the citation.
 * @property {string} note - What the source contributes to the rate card.
 */
export interface RateSource {
  id: string
  label: string
  url: string
  note: string
}

/**
 * Inputs driving {@link calculateUgcCost}.
 *
 * @interface UgcCostInputs
 * @property {number} creativesPerMonth - Finished paid-ready videos needed per month.
 * @property {CreatorTier} creatorTier - Organic base-rate tier.
 * @property {RightsTerm} rightsTerm - Usage-rights duration for paid ads.
 * @property {boolean} includeWhitelisting - Whether ads run from the creator handle.
 * @property {boolean} rawFootage - Whether raw footage is delivered for in-house edits.
 * @property {boolean} exclusivity - Category exclusivity add-on.
 * @property {Niche} niche - Vertical that may apply a premium multiplier.
 * @property {ProductionMode} productionMode - DIY / agency / hybrid AI path.
 * @property {number} expectedWinRatePct - Assumed % of creatives that become scalable winners (1–50).
 * @property {boolean} includeInternalOverhead - Apply 1.8×–2.4× all-in producer-hours multiplier.
 */
export interface UgcCostInputs {
  creativesPerMonth: number
  creatorTier: CreatorTier
  rightsTerm: RightsTerm
  includeWhitelisting: boolean
  rawFootage: boolean
  exclusivity: boolean
  niche: Niche
  productionMode: ProductionMode
  expectedWinRatePct: number
  includeInternalOverhead: boolean
}

/**
 * Line-item breakdown for one side of a USD range.
 *
 * @interface CostBreakdown
 * @property {UsdBand} basePerVideo - Organic base rate before uplifts.
 * @property {UsdBand} rightsUpliftPerVideo - Rights premium dollars per video.
 * @property {UsdBand} whitelistUpliftPerVideo - Whitelisting premium dollars per video.
 * @property {UsdBand} rawFootageUpliftPerVideo - Raw-footage premium dollars per video.
 * @property {UsdBand} exclusivityUpliftPerVideo - Exclusivity premium dollars per video.
 * @property {UsdBand} revisionsBufferMonthly - 10–20% revisions/rush buffer on the monthly subtotal.
 * @property {number} bundleDiscountPct - Volume discount applied when volume ≥ 5 (0–20).
 * @property {UsdBand} diyExternalMonthly - External creator spend only (after discount + buffer).
 * @property {UsdBand} diyAllInMonthly - External spend × 1.8–2.4× producer-hours (always computed).
 * @property {UsdBand} agencyMonthly - Agency / studio route band for the same volume.
 * @property {UsdBand} hybridMonthly - AI discovery + rebuild winners with creators (+ revisions).
 * @property {UsdBand} selectedMonthly - Band for the chosen production mode.
 * @property {UsdBand} costPerCreative - Selected monthly ÷ creatives.
 * @property {UsdBand} costPerWinner - Selected monthly ÷ expected winning finished videos (`∞` when 0).
 * @property {number} expectedWinners - finished videos × win-rate (raw; cost-per-winner denominator).
 * @property {number} hybridRebuildCount - Winner rebuild volume for hybrid (0 when win-rate is 0; else floored at 0.25).
 * @property {boolean} belowHealthyCadence - True when finished videos/month are under 6.
 * @property {boolean} agencyWrongBuy - True when volume is under ~4 (agency retainer usually wrong).
 * @property {boolean} agencyAboveTypicalRetainer - True when agency high exceeds the ~$15k typical retainer ceiling.
 * @property {'diy' | 'hybrid_rebuild' | 'agency_comparison'} stackMode - Which rate stack the per-video fields represent.
 */
export interface CostBreakdown {
  basePerVideo: UsdBand
  rightsUpliftPerVideo: UsdBand
  whitelistUpliftPerVideo: UsdBand
  rawFootageUpliftPerVideo: UsdBand
  exclusivityUpliftPerVideo: UsdBand
  revisionsBufferMonthly: UsdBand
  bundleDiscountPct: number
  diyExternalMonthly: UsdBand
  diyAllInMonthly: UsdBand
  agencyMonthly: UsdBand
  hybridMonthly: UsdBand
  selectedMonthly: UsdBand
  costPerCreative: UsdBand
  costPerWinner: UsdBand
  expectedWinners: number
  hybridRebuildCount: number
  belowHealthyCadence: boolean
  agencyWrongBuy: boolean
  agencyAboveTypicalRetainer: boolean
  stackMode: 'diy' | 'hybrid_rebuild' | 'agency_comparison'
}

/** Healthy Meta testing cadence: 6–10 finished videos/month as a planning floor. */
export const HEALTHY_MONTHLY_CONCEPTS_MIN = 6
export const HEALTHY_MONTHLY_CONCEPTS_MAX = 10

/** Typical agency retainer ceiling used for the high-volume warning (not a hard math cap). */
export const AGENCY_TYPICAL_RETAINER_HIGH = 15000

/** AI discovery band covers roughly this many variants before another unit is needed. */
export const AI_DISCOVERY_VARIANTS_PER_UNIT = 30

/**
 * Independent 2026 rate-card citations. Every band in this module is a blend
 * of these sources — nothing is invented for the calculator.
 */
export const RATE_SOURCES: readonly RateSource[] = [
  {
    id: 'influee-2026',
    label: 'Influee UGC Rates 2026 (via ChatCut / Social Native summaries)',
    url: 'https://chatcut.io/blog/how-to-become-a-ugc-creator-2026',
    note: 'Market average ~$198–$212 per deliverable; beginner / mid / top tier bands attributed to Influee UGC Rates 2026.',
  },
  {
    id: 'collabstr-2026',
    label: 'Collabstr UGC Price Calculator (1.2M+ packages)',
    url: 'https://collabstr.com/influencer-price-calculator/user-generated-content',
    note: 'Platform average ~$190 per UGC package in 2026 from marketplace data.',
  },
  {
    id: 'cliploft-2026',
    label: 'ClipLoft UGC Creator Pricing 2026',
    url: 'https://cliploft.com/blog/ugc-creator-pricing',
    note: 'Tier bands + rights/whitelist premiums. ClipLoft publishes 6-mo +25–50% and whitelist +30–100%; this calculator blends that with peer guides to 6-mo +25–100%, 12-mo +50–150%, perpetual +100–150%, and whitelist +25–50%.',
  },
  {
    id: 'creetr-2026',
    label: 'Creetr UGC Rates Guide 2026',
    url: 'https://creetr.com/blog/ugc-rates-guide',
    note: 'Entry $100–$200, intermediate $200–$400, experienced $400–$750, top $750–$2,000+; rights packages.',
  },
  {
    id: 'socialnative-2026',
    label: 'Social Native Influencer Pricing 2026',
    url: 'https://www.socialnative.com/articles/influencer-marketing-pricing-2026/',
    note: 'Average single UGC video ~$212; bundle discounts 10–25% at 3–10 videos (we use 10% at 5–9 and 15% at 10+ so DIY totals stay nondecreasing).',
  },
] as const

/**
 * Organic-only base rate per finished video by creator tier.
 * Blended from Influee, Collabstr, ClipLoft, Creetr, and Social Native 2026 guides.
 */
export const CREATOR_TIERS: Record<
  CreatorTier,
  { label: string; description: string; base: UsdBand }
> = {
  entry: {
    label: 'Entry / beginner',
    description: '0–1 year, organic volume and low-stakes feed tests',
    base: { low: 75, high: 200 },
  },
  mid: {
    label: 'Mid-tier',
    description: 'Reliable ad-ready footage; most paid DTC creative',
    base: { low: 300, high: 800 },
  },
  specialist: {
    label: 'Specialist / top-tier',
    description: 'Proven ad performance, trust-led categories',
    base: { low: 600, high: 1500 },
  },
}

/** Rights uplift as a fraction of base (low/high). Organic is +0%. */
export const RIGHTS_TERMS: Record<
  RightsTerm,
  { label: string; description: string; uplift: UsdBand }
> = {
  organic: {
    label: 'Organic only',
    description: 'Brand feed / organic posts — included in base',
    uplift: { low: 0, high: 0 },
  },
  paid_6mo: {
    label: '6-month paid rights',
    description: 'Run as paid ads for 6 months',
    uplift: { low: 0.25, high: 1.0 },
  },
  paid_12mo: {
    label: '12-month paid rights',
    description: 'Full-year paid usage across owned + paid channels',
    uplift: { low: 0.5, high: 1.5 },
  },
  perpetual: {
    label: 'Perpetual / unlimited',
    description: 'Forever paid usage (non-exclusive)',
    uplift: { low: 1.0, high: 1.5 },
  },
}

/** Niche multipliers applied to the organic base before rights. */
export const NICHES: Record<Niche, { label: string; multiplier: number }> = {
  general: { label: 'General / lifestyle', multiplier: 1.0 },
  beauty: { label: 'Beauty / skincare', multiplier: 1.15 },
  tech_saas: { label: 'Tech / SaaS', multiplier: 1.25 },
  finance: { label: 'Finance / fintech', multiplier: 1.2 },
  food: { label: 'Food / CPG', multiplier: 1.05 },
}

export const PRODUCTION_MODES: Record<
  ProductionMode,
  { label: string; description: string }
> = {
  diy_creators: {
    label: 'DIY creators',
    description: 'Brief freelancers directly; you own casting and revisions',
  },
  agency: {
    label: 'Agency / studio',
    description: 'Managed retainer with rights + revisions inside',
  },
  hybrid_ai: {
    label: 'Hybrid AI → creators',
    description: 'AI for volume discovery, rebuild only validated winners',
  },
}

/** Whitelisting premium as a fraction of base. */
const WHITELIST_UPLIFT: UsdBand = { low: 0.25, high: 0.5 }

/** Raw-footage uplift as a fraction of base (0 if agreed at briefing; up to 40%). */
const RAW_FOOTAGE_UPLIFT: UsdBand = { low: 0, high: 0.4 }

/** Category exclusivity uplift as a fraction of base. */
const EXCLUSIVITY_UPLIFT: UsdBand = { low: 0.3, high: 0.75 }

/** Revisions / rush buffer on the monthly external subtotal. */
const REVISIONS_BUFFER: UsdBand = { low: 0.1, high: 0.2 }

/** Internal producer-hours all-in multiplier on external DIY spend. */
const INTERNAL_OVERHEAD: UsdBand = { low: 1.8, high: 2.4 }

/** Agency per-deliverable band (rights + revisions inside). */
const AGENCY_PER_DELIVERABLE: UsdBand = { low: 250, high: 900 }

/** Agency monthly retainer floor / typical ceiling (ceiling is warning-only). */
const AGENCY_RETAINER: UsdBand = { low: 5000, high: AGENCY_TYPICAL_RETAINER_HIGH }

/** AI discovery cost per ~30-variant slate. */
const AI_DISCOVERY_MONTHLY: UsdBand = { low: 150, high: 350 }

/** Module-level USD formatter — avoid constructing Intl.NumberFormat on every slider tick. */
const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

/**
 * Formats a USD band as "$1,200 to $4,500" (or a single value when equal).
 * Non-finite bands (e.g. cost-per-winner with 0 expected winners) render as "∞".
 *
 * @param {UsdBand} band - Inclusive low/high USD band.
 * @returns {string} Human-readable currency range.
 */
export function formatUsdRange(band: UsdBand): string {
  if (!Number.isFinite(band.low) || !Number.isFinite(band.high)) return '∞'
  const fmt = (n: number): string => USD_FORMATTER.format(Math.round(n))
  if (band.low === band.high) return fmt(band.low)
  return `${fmt(band.low)} to ${fmt(band.high)}`
}

/**
 * Multiplies a USD band by a scalar.
 *
 * @param {UsdBand} band - Source band.
 * @param {number} factor - Multiplier applied to both ends.
 * @returns {UsdBand} Scaled band.
 */
function scaleBand(band: UsdBand, factor: number): UsdBand {
  return { low: band.low * factor, high: band.high * factor }
}

/**
 * Multiplies a USD band by a fractional uplift band (element-wise).
 *
 * @param {UsdBand} base - Base dollars.
 * @param {UsdBand} upliftFrac - Fractional uplift (e.g. 0.25–1.0).
 * @returns {UsdBand} Dollar uplift band.
 */
function upliftDollars(base: UsdBand, upliftFrac: UsdBand): UsdBand {
  return {
    low: base.low * upliftFrac.low,
    high: base.high * upliftFrac.high,
  }
}

/**
 * Adds two USD bands element-wise.
 *
 * @param {UsdBand} a - First band.
 * @param {UsdBand} b - Second band.
 * @returns {UsdBand} Sum band.
 */
function addBands(a: UsdBand, b: UsdBand): UsdBand {
  return { low: a.low + b.low, high: a.high + b.high }
}

/**
 * Volume discount for packages of 5+ videos (Social Native 10–25% band).
 * Uses 10% at 5–9 and 15% at 10+ — not 20% — so DIY monthly spend stays
 * nondecreasing when volume crosses 9 → 10 at constant per-video rates.
 *
 * @param {number} volume - Creatives per month.
 * @returns {number} Discount fraction in [0, 0.15].
 */
export function bundleDiscountFraction(volume: number): number {
  if (volume >= 10) return 0.15
  if (volume >= 5) return 0.1
  return 0
}

/**
 * Clamps creatives/month into a sane planning range.
 *
 * @param {number} n - Raw creatives count.
 * @returns {number} Integer in [1, 60].
 */
export function clampCreatives(n: number): number {
  if (!Number.isFinite(n)) return 1
  return Math.min(60, Math.max(1, Math.round(n)))
}

/**
 * Clamps expected win-rate percentage.
 *
 * @param {number} pct - Raw win-rate percent.
 * @returns {number} Value in [0, 50]. Zero is allowed so all-miss planning shows ∞ cost-per-winner.
 */
export function clampWinRate(pct: number): number {
  if (!Number.isFinite(pct)) return 15
  return Math.min(50, Math.max(0, pct))
}

/**
 * Estimates monthly UGC production cost across DIY, agency, and hybrid paths.
 *
 * DIY = (base × niche × volume) + rights + whitelist + raw + exclusivity,
 * minus bundle discount, plus revisions buffer; all-in always applies 1.8–2.4×
 * for the comparison panel, and the overhead toggle selects which DIY band is active.
 * Agency = max(retainer floor, per-deliverable × volume) with no hard high ceiling
 * (a warning flag fires above the typical ~$15k retainer).
 * Hybrid = volume-scaled AI discovery + rebuild expected winners at mid-tier with
 * the same rights/whitelist/raw/exclusivity stack and a revisions buffer.
 *
 * @param {UgcCostInputs} inputs - Calculator inputs.
 * @returns {CostBreakdown} Full breakdown for the UI and CSV export.
 * @example
 * const result = calculateUgcCost({
 *   creativesPerMonth: 8,
 *   creatorTier: 'mid',
 *   rightsTerm: 'paid_6mo',
 *   includeWhitelisting: false,
 *   rawFootage: false,
 *   exclusivity: false,
 *   niche: 'general',
 *   productionMode: 'diy_creators',
 *   expectedWinRatePct: 15,
 *   includeInternalOverhead: false,
 * })
 * // result.diyExternalMonthly ≈ $2,970–$13,824 (mid-tier + 6-mo rights, 10% bundle, revisions buffer)
 */
export function calculateUgcCost(inputs: UgcCostInputs): CostBreakdown {
  const volume = clampCreatives(inputs.creativesPerMonth)
  const winRate = clampWinRate(inputs.expectedWinRatePct) / 100
  const nicheMult = NICHES[inputs.niche].multiplier
  const baseRaw = CREATOR_TIERS[inputs.creatorTier].base
  const basePerVideo = scaleBand(baseRaw, nicheMult)

  const rightsFrac = RIGHTS_TERMS[inputs.rightsTerm].uplift
  const rightsUpliftPerVideo = upliftDollars(basePerVideo, rightsFrac)

  const whitelistUpliftPerVideo = inputs.includeWhitelisting
    ? upliftDollars(basePerVideo, WHITELIST_UPLIFT)
    : { low: 0, high: 0 }

  const rawFootageUpliftPerVideo = inputs.rawFootage
    ? upliftDollars(basePerVideo, RAW_FOOTAGE_UPLIFT)
    : { low: 0, high: 0 }

  const exclusivityUpliftPerVideo = inputs.exclusivity
    ? upliftDollars(basePerVideo, EXCLUSIVITY_UPLIFT)
    : { low: 0, high: 0 }

  const perVideoLoaded = [
    basePerVideo,
    rightsUpliftPerVideo,
    whitelistUpliftPerVideo,
    rawFootageUpliftPerVideo,
    exclusivityUpliftPerVideo,
  ].reduce(addBands)

  const monthlyGross = scaleBand(perVideoLoaded, volume)
  const discount = bundleDiscountFraction(volume)
  const afterDiscount = scaleBand(monthlyGross, 1 - discount)
  const revisionsBufferMonthly = upliftDollars(afterDiscount, REVISIONS_BUFFER)
  const diyExternalMonthly = addBands(afterDiscount, revisionsBufferMonthly)

  // Always compute the all-in band so the comparison panel stays honest when the
  // overhead toggle is off (default). The toggle only selects which DIY band is active.
  const diyAllInMonthly: UsdBand = {
    low: diyExternalMonthly.low * INTERNAL_OVERHEAD.low,
    high: diyExternalMonthly.high * INTERNAL_OVERHEAD.high,
  }

  // Agency: per-deliverable × volume, floored at retainer min. No hard high-cap —
  // deliverable math scales with volume; we warn when above the typical $15k ceiling.
  const agencyFromDeliverables = scaleBand(AGENCY_PER_DELIVERABLE, volume)
  const agencyMonthly: UsdBand = {
    low: Math.max(AGENCY_RETAINER.low, agencyFromDeliverables.low),
    high: Math.max(AGENCY_RETAINER.low, agencyFromDeliverables.high),
  }
  const agencyAboveTypicalRetainer = agencyMonthly.high > AGENCY_TYPICAL_RETAINER_HIGH

  // Raw expected winners drive cost-per-winner. Hybrid rebuild volume floors at
  // 0.25 for fractional win-rates, but stays 0 when the user models an all-miss slate.
  const expectedWinners = volume * winRate
  const hybridRebuildCount =
    expectedWinners === 0 ? 0 : Math.max(0.25, expectedWinners)

  // Hybrid: AI discovery scales with volume (~30 variants per $150–$350 unit);
  // rebuild winners at mid-tier + rights stack + revisions buffer.
  const discoveryUnits = Math.max(1, volume / AI_DISCOVERY_VARIANTS_PER_UNIT)
  const aiDiscoveryMonthly = scaleBand(AI_DISCOVERY_MONTHLY, discoveryUnits)
  const winnerRebuildBase = scaleBand(CREATOR_TIERS.mid.base, nicheMult)
  const winnerWhitelist = inputs.includeWhitelisting
    ? upliftDollars(winnerRebuildBase, WHITELIST_UPLIFT)
    : { low: 0, high: 0 }
  const winnerRaw = inputs.rawFootage
    ? upliftDollars(winnerRebuildBase, RAW_FOOTAGE_UPLIFT)
    : { low: 0, high: 0 }
  const winnerExclusivity = inputs.exclusivity
    ? upliftDollars(winnerRebuildBase, EXCLUSIVITY_UPLIFT)
    : { low: 0, high: 0 }
  const winnerPerVideo = [
    winnerRebuildBase,
    upliftDollars(winnerRebuildBase, rightsFrac),
    winnerWhitelist,
    winnerRaw,
    winnerExclusivity,
  ].reduce(addBands)
  const winnerRebuildMonthly = scaleBand(winnerPerVideo, hybridRebuildCount)
  const winnerRebuildWithRevisions = addBands(
    winnerRebuildMonthly,
    upliftDollars(winnerRebuildMonthly, REVISIONS_BUFFER),
  )
  const hybridMonthly = addBands(aiDiscoveryMonthly, winnerRebuildWithRevisions)

  let selectedMonthly: UsdBand
  switch (inputs.productionMode) {
    case 'diy_creators':
      selectedMonthly = inputs.includeInternalOverhead
        ? diyAllInMonthly
        : diyExternalMonthly
      break
    case 'agency':
      selectedMonthly = agencyMonthly
      break
    case 'hybrid_ai':
      selectedMonthly = hybridMonthly
      break
    default: {
      const _exhaustive: never = inputs.productionMode
      throw new Error(`Unhandled production mode: ${String(_exhaustive)}`)
    }
  }

  const costPerCreative = {
    low: selectedMonthly.low / volume,
    high: selectedMonthly.high / volume,
  }
  const costPerWinner: UsdBand =
    expectedWinners === 0
      ? { low: Number.POSITIVE_INFINITY, high: Number.POSITIVE_INFINITY }
      : {
          low: selectedMonthly.low / expectedWinners,
          high: selectedMonthly.high / expectedWinners,
        }

  // Mode-effective stack for the per-video panel: Hybrid shows mid-tier rebuild
  // rates (what hybridMonthly actually prices). Agency keeps the DIY stack as a
  // comparison reference. DIY shows the selected creator tier.
  const winnerRights = upliftDollars(winnerRebuildBase, rightsFrac)
  const winnerRevisionsMonthly = upliftDollars(winnerRebuildMonthly, REVISIONS_BUFFER)
  let stackMode: CostBreakdown['stackMode']
  let stackBase: UsdBand
  let stackRights: UsdBand
  let stackWhitelist: UsdBand
  let stackRaw: UsdBand
  let stackExclusivity: UsdBand
  let stackRevisions: UsdBand
  switch (inputs.productionMode) {
    case 'hybrid_ai':
      stackMode = 'hybrid_rebuild'
      stackBase = winnerRebuildBase
      stackRights = winnerRights
      stackWhitelist = winnerWhitelist
      stackRaw = winnerRaw
      stackExclusivity = winnerExclusivity
      stackRevisions = winnerRevisionsMonthly
      break
    case 'agency':
      stackMode = 'agency_comparison'
      stackBase = basePerVideo
      stackRights = rightsUpliftPerVideo
      stackWhitelist = whitelistUpliftPerVideo
      stackRaw = rawFootageUpliftPerVideo
      stackExclusivity = exclusivityUpliftPerVideo
      stackRevisions = revisionsBufferMonthly
      break
    case 'diy_creators':
      stackMode = 'diy'
      stackBase = basePerVideo
      stackRights = rightsUpliftPerVideo
      stackWhitelist = whitelistUpliftPerVideo
      stackRaw = rawFootageUpliftPerVideo
      stackExclusivity = exclusivityUpliftPerVideo
      stackRevisions = revisionsBufferMonthly
      break
    default: {
      const _exhaustive: never = inputs.productionMode
      throw new Error(`Unhandled production mode: ${String(_exhaustive)}`)
    }
  }

  return {
    basePerVideo: stackBase,
    rightsUpliftPerVideo: stackRights,
    whitelistUpliftPerVideo: stackWhitelist,
    rawFootageUpliftPerVideo: stackRaw,
    exclusivityUpliftPerVideo: stackExclusivity,
    revisionsBufferMonthly: stackRevisions,
    bundleDiscountPct: discount * 100,
    diyExternalMonthly,
    diyAllInMonthly,
    agencyMonthly,
    hybridMonthly,
    selectedMonthly,
    costPerCreative,
    costPerWinner,
    expectedWinners,
    hybridRebuildCount,
    belowHealthyCadence: volume < HEALTHY_MONTHLY_CONCEPTS_MIN,
    agencyWrongBuy: volume < 4,
    agencyAboveTypicalRetainer,
    stackMode,
  }
}
